import type { Feature, FeatureCollection, Polygon } from "geojson";
import { isPolygonValid, polygonContains } from "../../../domain/geometry/operations/validate";
import type { AnyPolygonFeature, GeometryResult } from "../../../domain/geometry/types";
import type { UndoableCommand } from "../undoStack";

export interface DrawBoundaryResult {
	command: UndoableCommand;
	boundaryFC: FeatureCollection;
}

/**
 * Validate a drawn polygon and either add it as a hole (if inside an existing
 * boundary) or append it as a new feature.
 */
export function drawBoundaryUseCase(
	drawn: Feature<Polygon>,
	existingFC: FeatureCollection,
	applyResult: (fc: FeatureCollection) => void,
	revert: (fc: FeatureCollection) => void,
): GeometryResult<DrawBoundaryResult> {
	const validation = isPolygonValid(drawn);
	if (!validation.isValid) {
		return { ok: false, error: validation.message ?? "Invalid polygon" };
	}

	// Check if drawn is fully inside an existing feature → add as hole
	const containingIdx = existingFC.features.findIndex((f) => polygonContains(f as AnyPolygonFeature, drawn));

	let newFC: FeatureCollection;
	let label: string;

	if (containingIdx >= 0) {
		const target = existingFC.features[containingIdx] as Feature<Polygon>;
		const withHole: Feature<Polygon> = {
			...target,
			// Don't spread target.geometry — it may carry a stale bbox that breaks turf
			geometry: {
				type: "Polygon" as const,
				coordinates: [...target.geometry.coordinates, drawn.geometry.coordinates[0]],
			},
		};
		const holeValidation = isPolygonValid(withHole);
		if (!holeValidation.isValid) {
			return { ok: false, error: holeValidation.message ?? "Invalid hole" };
		}
		newFC = {
			...existingFC,
			features: existingFC.features.map((f, i) => (i === containingIdx ? withHole : f)),
		};
		label = "Add boundary hole";
	} else {
		newFC = { ...existingFC, features: [...existingFC.features, drawn] };
		label = "Draw boundary";
	}

	return {
		ok: true,
		data: {
			boundaryFC: newFC,
			command: {
				label,
				execute: () => applyResult(newFC),
				undo: () => revert(existingFC),
			},
		},
	};
}
